import { ButtonWithLoader } from "@/components/common/buttons";
import { useLanguage, useCountryShort, useRouteSlug } from "@/hooks";
import {
  authenticateUser,
  resetRegistrationDataStatus
} from "@/redux/actions/homepage.action";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { useFormik } from "formik";
import { Trans, useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import * as Yup from "yup";
import { PasswordInput } from "./passwordInput";
import { useRef, useState } from "react";
import cn from "clsx";
import CommonAnalyticsEvent from "@/services/analytics/main/common";
import {
  TurnstileCaptcha,
  TurnstileCaptchaRef
} from "@/components/captcha/captcha";
import { getConfigurationSettings } from "@/redux/actions/common.action";
import { selectSettings } from "@/util/selectors";

export const PasswordPanel: React.FC<{
  email: string;
  onEditEmail: () => void;
  onAuthMissingFieldsError: any;
  onAuthCallback: any;
}> = ({ email, onEditEmail, onAuthCallback, onAuthMissingFieldsError }) => {
  const [serverError, setServerError] = useState<string>("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [isInteractiveCaptcha, setIsInteractiveCaptcha] = useState(false);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const language = useLanguage();
  const countrySlug = useCountryShort();
  const routeSlug = useRouteSlug();
  const { t } = useTranslation(["login", "auth"]);
  const turnstileCaptchaToken = useAppSelector(
    state => state.authReducer.turnstileCaptchaToken
  );
  const settings = useAppSelector(selectSettings);
  const valueCaptchaFromCMS = settings?.captcha?.passwordLoginScreen;
  const captchaRef = useRef<TurnstileCaptchaRef>(null);
  const isAutomaticCaptcha =
    !captchaLoading &&
    !isInteractiveCaptcha &&
    valueCaptchaFromCMS &&
    !turnstileCaptchaToken;
  const shouldShowLoader = isAutomaticCaptcha || captchaLoading;
  const shouldButtonDisabled = valueCaptchaFromCMS && !turnstileCaptchaToken;

  const formik = useFormik<{ password: string }>({
    initialValues: { password: "" },
    validationSchema: Yup.object().shape({
      password: Yup.string().trim().required()
    }),
    onSubmit: async ({ password }, helpers) => {
      setServerError("");
      if (!email) {
        helpers.setSubmitting(false);
        return;
      }
      const res: any = await dispatch(
        authenticateUser({
          bodyData: { email, password: password.trim() },
          history,
          language,
          redirect: false,
          country: countrySlug,
          onAuth: onAuthCallback,
          withCartRedirect: false,
          onAuthMissingFieldsError
        })
      );
      dispatch(resetRegistrationDataStatus());
      if (!res?.success) {
        captchaRef?.current?.reset();
        setServerError(res?.message || t("somethingWentWrong"));
      }
      helpers.setSubmitting(false);
    }
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldTouched
  } = formik;

  const isPwdInvalid = Boolean(touched.password && errors.password);
  const isDisabled = isSubmitting || !values.password.trim();

  return (
    <div className="panel">
      <span className="panel_title">{t("auth:enterPassword")}</span>
      <p className="panel_subtitle">
        <Trans
          i18nKey="auth:loginWithPassword"
          values={{ email }}
          components={[<span />]}
        />{" "}
        <button type="button" className="link" onClick={onEditEmail}>
          {t("auth:edit")}
        </button>
      </p>

      <form onSubmit={handleSubmit}>
        <PasswordInput
          value={values.password}
          onChange={e => {
            if (!touched.password) setFieldTouched("password", true, false);
            if (serverError) setServerError("");
            handleChange(e);
          }}
          invalid={isPwdInvalid}
        />

        {isPwdInvalid && (
          <span id="password-error" className="error" role="alert">
            {t("login:required")}
          </span>
        )}

        <Link
          className="forgot_link"
          to={`/${routeSlug}/forgot-password/`}
          onClick={() =>
            CommonAnalyticsEvent.clickToAction({
              label: t("forgotPassword", { lng: "en" })
            })
          }
        >
          {t("auth:forgotPassword")}
        </Link>

        {serverError ? (
          <div className="error error_above_btn" role="alert">
            {serverError}
          </div>
        ) : null}
        {valueCaptchaFromCMS && (
          <TurnstileCaptcha
            ref={captchaRef}
            className="mt-4 mb-4"
            beforeLoading={() => setCaptchaLoading(true)}
            afterLoading={() => {
              setCaptchaLoading(false);
            }}
            onInterectiveChallangeStarted={() => {
              setIsInteractiveCaptcha(true);
            }}
            onToken={() => setCaptchaLoading(false)}
            onTokenExpired={() => setCaptchaLoading(false)}
          />
        )}

        <ButtonWithLoader
          type="submit"
          className={cn("btn_black")}
          showLoaderWithoutClick={true}
          showLoader={shouldShowLoader}
          disabled={
            isSubmitting || Boolean(shouldShowLoader) || shouldButtonDisabled
          }
          aria-disabled={isDisabled}
        >
          {!shouldShowLoader && t("signIn")}
        </ButtonWithLoader>
      </form>
    </div>
  );
};
